import {
    Box,
    Button,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Drawer,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import 'dayjs/locale/ko';
import Magnify from 'mdi-material-ui/Magnify';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';

const DialogBooking = (props) => {
    const { bookingID, show, setShow, setReload } = props;
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState(null);
    const [user, setUser] = useState(null);
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const defaultValues = {
        id: booking?.id,
        username: booking?.username,
        sku: booking?.sku,
        intime: booking?.intime,
        uptime: booking?.uptime,
    }

    const getBooking = (id) => {
        const fetchData = async () => {
            try {
                const response = await apiService.get(`/booking/${id}`);
                const payload = response.data;
                setBooking(payload.data);
            } catch (e) {
                console.error('Error fetching booking:', e);
            }
        }

        fetchData();
    }

    const getUser = (id) => {
        const fetchUser = async () => {
            try {
                const response = await apiService.get(`/users/find/${id}`);
                const payload = response.data;
                setUser(payload.data);
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };

        fetchUser();
    }

    useEffect(() => {
        reset(defaultValues);
        booking && getUser(booking.username);        
    }, [booking])

    useEffect(() => {
        if(show) {
            bookingID && getBooking(bookingID);
        } else {
            reset(defaultValues);
        }
    }, [show])

    const changeBooking = async (id, value) => {
        try {
            const response = await apiService.put(`/booking/${id}`, {
                intime: value
            });
            toast.success('수정 되었습니다');
            setReload(reload => reload + 1);
        } catch (error) {
            console.error('Error updating booking:', error);
        } finally {
            setLoading(false);
            setShow(false);
        }
    }

    const onSubmit = async (data, e) => {
        e.preventDefault();

        const val = dayjs(data.intime).format("YYYY-MM-DD");

        setLoading(true)
        changeBooking(booking.id, val);
    }

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShow(open);
    };

    return (
        <Drawer anchor='right' open={show} onClose={toggleDrawer(false)}>
            <Container sx={{width: '800px', pt: 8, px: '2rem !important'}}>
                <form onSubmit={handleSubmit(onSubmit)} id="bookingform">
                    <CardContent sx={{ pt: 8 }}>
                        <Box sx={{ mb: 9, textAlign: 'center' }}>
                            <Typography variant='h5' sx={{ mb: 3, lineHeight: '2rem' }}>
                                입고 알림
                            </Typography>
                        </Box>

                        <Grid container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='sku'
                                        control={control}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                disabled
                                                label='SKU'
                                                id='sku'
                                                onChange={onChange}
                                                placeholder='이름을 입력해주세요'
                                                error={Boolean(errors.sku)}
                                                aria-describedby='validation-async-sku'
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='username'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <Paper component="div" sx={{ display: 'flex', alignItems: 'center' }} >
                                                <TextField
                                                    value={value}
                                                    fullWidth
                                                    disabled
                                                    label='고객ID'
                                                    id='username'
                                                    onChange={onChange}
                                                    placeholder='이름을 입력해주세요'
                                                    error={Boolean(errors.name)}
                                                    aria-describedby='validation-async-name'
                                                />
                                                <Link to={`/admin/member/${user?.userid}`} >
                                                    <IconButton type="button" aria-label="search" sx={{ml: 2}}>
                                                        <Magnify />
                                                    </IconButton>
                                                </Link>
                                            </Paper>
                                        )}
                                    />
                                    {errors.name && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-name'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='intime'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=""
                                        render={({ field: { value, onChange } }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                                                <DatePicker
                                                    label='입고날짜'
                                                    value={value}
                                                    onChange={onChange}
                                                    inputFormat={dayjs(value).format("YYYY-MM-DD")}
                                                    renderInput={(params) => <TextField {...params} />}
                                                />
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.category && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-category'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <CustomChip
                                    skin='light'
                                    color='info'
                                    label={`등록날짜: ${booking?.uptime}`}
                                    sx={{ '& .MuiChip-label': { color: 'common.black' } }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>

                <CardActions className='card-action'>
                    <Button type="submit" form="bookingform" variant='contained'>
                        { loading ? (
                            <CircularProgress
                                sx={{
                                color: 'common.white',
                                width: '20px !important',
                                height: '20px !important',
                                mr: theme => theme.spacing(2)
                                }}
                            />
                            ) : null
                        }
                        수정 
                    </Button>
                    <Button variant='outlined' color='secondary' onClick={() => setShow(false)}> 취소 </Button>
                </CardActions>
            </Container>
        </Drawer>
    )
}

export default DialogBooking